import type { LoxoKpiRow, LoxoScoringConfig } from './api'

export type TargetMode = 'static' | 'blended' | 'percentile' | 'regression'

// (userId, kpiKey, month) → target value or null
export type TargetResolver = (
  userId: number,
  kpiKey: string,
  month: string
) => number | null

export type DynamicTargetParams = {
  blendAlpha: number // 0-1, default 0.4 (weight toward personal avg)
  trimPercent: number // 0-50, default 10 (% to trim from each end)
  maxStretch: number // 0-1, default 0.15 (how far above current percentile to target)
  regressionDecay: number // 0-1, default 0.5 (strength of pull toward median)
}

export const defaultDynamicParams: DynamicTargetParams = {
  blendAlpha: 0.4,
  trimPercent: 10,
  maxStretch: 0.15,
  regressionDecay: 0.5,
}

type KpiStats = {
  userAvgs: Map<number, number> // userId → avg actual
  sortedAvgs: number[] // all user avgs sorted ascending
  groupMedian: number
  seasonality: Map<string, number> // month → index (1.0 = average)
}

function buildKpiUserStats(
  loxoData: LoxoKpiRow[],
  configs: LoxoScoringConfig[],
  months: string[]
): Map<string, KpiStats> {
  const result = new Map<string, KpiStats>()

  for (const config of configs) {
    const kpiKey = config.key

    // Group raw data: month → user → summed actual
    const monthUserActuals = new Map<string, Map<number, number>>()
    for (const row of loxoData) {
      if (row.kpi !== kpiKey) continue
      const month = months.find((m) => row.period.startsWith(m))
      if (!month) continue
      if (!monthUserActuals.has(month)) monthUserActuals.set(month, new Map())
      const userMap = monthUserActuals.get(month)!
      userMap.set(row.user_id, (userMap.get(row.user_id) ?? 0) + row.value)
    }

    // Per user: average actual across months where active (actual > 0)
    const userTotals = new Map<number, { sum: number; count: number }>()
    monthUserActuals.forEach((userMap) => {
      userMap.forEach((actual: number, userId: number) => {
        if (actual > 0) {
          const entry = userTotals.get(userId) ?? { sum: 0, count: 0 }
          entry.sum += actual
          entry.count++
          userTotals.set(userId, entry)
        }
      })
    })

    const userAvgs = new Map<number, number>()
    userTotals.forEach(({ sum, count }, userId) => {
      userAvgs.set(userId, sum / count)
    })

    const sortedAvgs = Array.from(userAvgs.values()).sort((a, b) => a - b)

    const groupMedian =
      sortedAvgs.length === 0
        ? 0
        : sortedAvgs.length % 2 === 1
          ? sortedAvgs[Math.floor(sortedAvgs.length / 2)]
          : (sortedAvgs[sortedAvgs.length / 2 - 1] +
              sortedAvgs[sortedAvgs.length / 2]) /
            2

    // Seasonality: per-month index (1.0 = average)
    let totalActuals = 0
    let totalActiveUserMonths = 0
    const monthStats = new Map<string, { sum: number; activeUsers: number }>()

    for (const m of months) {
      const userMap = monthUserActuals.get(m)
      let monthSum = 0
      let activeUsers = 0
      if (userMap) {
        userMap.forEach((actual) => {
          if (actual > 0) {
            monthSum += actual
            activeUsers++
          }
        })
      }
      monthStats.set(m, { sum: monthSum, activeUsers })
      totalActuals += monthSum
      totalActiveUserMonths += activeUsers
    }

    const overallAvg =
      totalActiveUserMonths > 0 ? totalActuals / totalActiveUserMonths : 0

    const seasonality = new Map<string, number>()
    for (const m of months) {
      const stats = monthStats.get(m)!
      if (stats.activeUsers === 0 || overallAvg === 0) {
        seasonality.set(m, 1.0) // default to neutral if no data
      } else {
        seasonality.set(m, stats.sum / stats.activeUsers / overallAvg)
      }
    }

    result.set(kpiKey, { userAvgs, sortedAvgs, groupMedian, seasonality })
  }

  return result
}

function computeBlendedTargets(
  stats: Map<string, KpiStats>,
  params: DynamicTargetParams
): TargetResolver {
  const cache = new Map<string, number | null>()

  // Pre-compute trimmed group avg per KPI
  const trimmedAvgs = new Map<string, number>()
  stats.forEach((kpi, key) => {
    const arr = kpi.sortedAvgs
    if (arr.length === 0) {
      trimmedAvgs.set(key, 0)
      return
    }
    const trimCount = Math.floor((params.trimPercent / 100) * arr.length)
    const trimmed = arr.slice(trimCount, arr.length - trimCount)
    if (trimmed.length === 0) {
      trimmedAvgs.set(key, arr.reduce((a, b) => a + b, 0) / arr.length)
      return
    }
    trimmedAvgs.set(key, trimmed.reduce((a, b) => a + b, 0) / trimmed.length)
  })

  return (userId, kpiKey, month) => {
    const cacheKey = `${userId}-${kpiKey}-${month}`
    if (cache.has(cacheKey)) return cache.get(cacheKey)!

    const kpi = stats.get(kpiKey)
    if (!kpi) {
      cache.set(cacheKey, null)
      return null
    }

    const userAvg = kpi.userAvgs.get(userId)
    if (userAvg == null) {
      cache.set(cacheKey, null)
      return null
    }

    const groupAvg = trimmedAvgs.get(kpiKey) ?? 0
    const alpha = params.blendAlpha
    const baseTarget = alpha * userAvg + (1 - alpha) * groupAvg
    const seasonIndex = kpi.seasonality.get(month) ?? 1.0
    const target = baseTarget * seasonIndex

    cache.set(cacheKey, target)
    return target
  }
}

function interpolatePercentile(sortedAvgs: number[], pct: number): number {
  if (sortedAvgs.length === 0) return 0
  if (sortedAvgs.length === 1) return sortedAvgs[0]
  const idx = pct * (sortedAvgs.length - 1)
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sortedAvgs[lo]
  const frac = idx - lo
  return sortedAvgs[lo] * (1 - frac) + sortedAvgs[hi] * frac
}

function computePercentileTargets(
  stats: Map<string, KpiStats>,
  params: DynamicTargetParams
): TargetResolver {
  const cache = new Map<string, number | null>()

  return (userId, kpiKey, month) => {
    const cacheKey = `${userId}-${kpiKey}-${month}`
    if (cache.has(cacheKey)) return cache.get(cacheKey)!

    const kpi = stats.get(kpiKey)
    if (!kpi) {
      cache.set(cacheKey, null)
      return null
    }

    const userAvg = kpi.userAvgs.get(userId)
    if (userAvg == null) {
      cache.set(cacheKey, null)
      return null
    }

    const totalUsers = kpi.sortedAvgs.length
    if (totalUsers === 0) {
      cache.set(cacheKey, null)
      return null
    }

    // Find rank (0-based index in sorted array)
    const rank = kpi.sortedAvgs.filter((v) => v < userAvg).length
    const currentPct = rank / totalUsers
    const targetPct = Math.min(
      1,
      currentPct + params.maxStretch * (1 - currentPct)
    )
    const targetValue = interpolatePercentile(kpi.sortedAvgs, targetPct)

    const seasonIndex = kpi.seasonality.get(month) ?? 1.0
    const target = targetValue * seasonIndex

    cache.set(cacheKey, target)
    return target
  }
}

function computeRegressionTargets(
  stats: Map<string, KpiStats>,
  params: DynamicTargetParams
): TargetResolver {
  const cache = new Map<string, number | null>()

  return (userId, kpiKey, month) => {
    const cacheKey = `${userId}-${kpiKey}-${month}`
    if (cache.has(cacheKey)) return cache.get(cacheKey)!

    const kpi = stats.get(kpiKey)
    if (!kpi) {
      cache.set(cacheKey, null)
      return null
    }

    const userAvg = kpi.userAvgs.get(userId)
    if (userAvg == null) {
      cache.set(cacheKey, null)
      return null
    }

    const regressed =
      kpi.groupMedian * params.regressionDecay +
      userAvg * (1 - params.regressionDecay)
    const baseTarget = Math.max(userAvg, regressed)
    const seasonIndex = kpi.seasonality.get(month) ?? 1.0
    const target = baseTarget * seasonIndex

    cache.set(cacheKey, target)
    return target
  }
}

export function buildTargetResolver(
  mode: TargetMode,
  loxoData: LoxoKpiRow[],
  configs: LoxoScoringConfig[],
  months: string[],
  params: DynamicTargetParams
): TargetResolver | null {
  if (mode === 'static') return null

  const stats = buildKpiUserStats(loxoData, configs, months)

  switch (mode) {
    case 'blended':
      return computeBlendedTargets(stats, params)
    case 'percentile':
      return computePercentileTargets(stats, params)
    case 'regression':
      return computeRegressionTargets(stats, params)
  }
}
